// JOBE-0101: useEntityBrowserUrlState — two-way bridge between an
// EntityBrowser instance's filter / sort / cursor / view state and the
// URL via react-router-dom's useSearchParams.
//
// Relocated and renamed from `library/useLibraryUrlState.js` per spec
// §Decision 3. The behaviour is preserved verbatim when `namespace` is
// unset (the library passes nothing); when `namespace` is set every
// URL key the hook owns is prefixed `<namespace>.<key>=…` so multiple
// instances on one page do not collide.
//
// Param set under the namespace (spec §Decision 3 table):
//   - chips: every `FRIENDLY_TOKENS[*].urlParam` plus dynamic `field.<attr>`
//   - layout / paging: `sort`, `cursor`, `view`
//
// Parsing rule (load-bearing): the prefix is split on the FIRST `.`
// only — `tasks.field.priority=low` resolves to namespace `tasks` and
// chip key `field.priority`. Implemented in chipVocabulary.js's
// chipsFromSearchParams when `{namespace}` is set.
//
// Namespace constraint: the namespace string must be non-empty and
// must not contain `.`. Composition mistakes throw at mount time
// rather than silently mis-routing URL params.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  chipsFromSearchParams,
  chipsToSearchParams,
} from '../library/chipVocabulary.js';

const URL_WRITE_DEBOUNCE_MS = 200;
const VALID_SORTS = new Set([
  'lastChangedAt:desc', 'lastChangedAt:asc', 'name:asc', 'name:desc',
]);

function assertNamespace(namespace) {
  if (namespace === undefined || namespace === null) return;
  if (typeof namespace !== 'string' || namespace.length === 0) {
    throw new Error(
      'useEntityBrowserUrlState: urlStateNamespace must be a non-empty string '
        + '(or omitted to preserve today\'s unprefixed URL shape).',
    );
  }
  if (namespace.indexOf('.') >= 0) {
    throw new Error(
      `useEntityBrowserUrlState: urlStateNamespace "${namespace}" must not `
        + 'contain a "." — the namespace prefix is split on the first dot and a '
        + 'dotted namespace would silently mis-route URL params.',
    );
  }
}

// hydrateFromSearchParams: pure read of the URL into the page-state
// shape this hook exposes. Honours the optional namespace prefix.
function hydrateFromSearchParams(searchParams, namespace) {
  const chips = chipsFromSearchParams(searchParams, { namespace });
  const prefix = namespace ? `${namespace}.` : '';
  const sortRaw = searchParams.get(`${prefix}sort`);
  const sort = VALID_SORTS.has(sortRaw) ? sortRaw : 'lastChangedAt:desc';
  const cursor = searchParams.get(`${prefix}cursor`) || null;
  const view = searchParams.get(`${prefix}view`) || null;
  return { chips, sort, cursor, view };
}

// serializeToSearchParams: pure write of the page state into a
// URLSearchParams. Omits `cursor` when null; omits `sort` when default.
//
// When namespaced, this function ONLY emits the keys it owns. Callers
// that want to preserve unrelated URL params (e.g. a sibling instance's
// namespaced keys) merge in the existing params separately at write
// time — see the writer effect below.
function serializeToSearchParams(state, namespace) {
  const params = chipsToSearchParams(state.chips, { namespace });
  const prefix = namespace ? `${namespace}.` : '';
  if (state.sort && state.sort !== 'lastChangedAt:desc') {
    params.set(`${prefix}sort`, state.sort);
  }
  if (state.cursor) {
    params.set(`${prefix}cursor`, state.cursor);
  }
  if (state.view) {
    params.set(`${prefix}view`, state.view);
  }
  return params;
}

// ownedKeys: every URL param key this instance would emit at full
// occupancy. Used by the writer to know which keys to clear on commit
// without trampling another instance's namespaced keys.
function isOwnedKey(key, namespace) {
  const prefix = namespace ? `${namespace}.` : '';
  if (prefix && !key.startsWith(prefix)) return false;
  const stripped = prefix ? key.slice(prefix.length) : key;
  if (stripped === 'sort' || stripped === 'cursor' || stripped === 'view') return true;
  if (stripped.startsWith('field.')) return true;
  // FRIENDLY_TOKENS urlParam set — read lazily to dodge a circular import.
  return KNOWN_FRIENDLY_URL_PARAMS.has(stripped);
}

// Pre-built table of `FRIENDLY_TOKENS[*].urlParam` values. Built lazily
// to keep this module a clean import-time peer of chipVocabulary.
import { FRIENDLY_TOKENS } from '../library/chipVocabulary.js';
const KNOWN_FRIENDLY_URL_PARAMS = new Set(
  Object.values(FRIENDLY_TOKENS).map((entry) => entry.urlParam),
);

export function useEntityBrowserUrlState({ namespace } = {}) {
  // Validate at mount time so composition mistakes surface immediately.
  // Throw BEFORE any hook call — React only requires hook order to be
  // stable across renders that complete; an unconditional throw on the
  // very first render is fine and surfaces synchronously to the caller.
  assertNamespace(namespace);

  const [searchParams, setSearchParams] = useSearchParams();

  // Hydrate on first mount only. Subsequent URL changes (back/forward,
  // external link) are picked up by the searchParams effect below.
  const [state, setState] = useState(() =>
    hydrateFromSearchParams(searchParams, namespace),
  );

  // React to URL changes initiated by the browser (back/forward).
  // Compare against the serialized current state; if the namespaced
  // slice diverges, hydrate. Avoids infinite write/read loops by only
  // hydrating when the URL is the source of truth (i.e., not from our
  // debounced write).
  const lastWrittenRef = useRef(serializeToSearchParams(state, namespace).toString());
  useEffect(() => {
    // Project the current URL onto the keys this instance owns so a
    // sibling instance's writes don't trigger a spurious rehydrate here.
    const ownedSlice = new URLSearchParams();
    for (const [key, raw] of searchParams.entries()) {
      if (isOwnedKey(key, namespace)) ownedSlice.set(key, raw);
    }
    const sliceStr = ownedSlice.toString();
    if (sliceStr === lastWrittenRef.current) return;
    setState(hydrateFromSearchParams(searchParams, namespace));
    lastWrittenRef.current = sliceStr;
  }, [searchParams, namespace]);

  // Debounced URL writer. When namespaced, this preserves all keys it
  // doesn't own (i.e., other instances' namespaces) and only mutates
  // the keys belonging to this instance.
  const writeTimerRef = useRef(null);
  useEffect(() => {
    const owned = serializeToSearchParams(state, namespace);
    const str = owned.toString();
    if (str === lastWrittenRef.current) return;
    if (writeTimerRef.current) clearTimeout(writeTimerRef.current);
    writeTimerRef.current = setTimeout(() => {
      lastWrittenRef.current = str;
      // Merge: keep keys we don't own, then layer in our owned keys.
      const merged = new URLSearchParams();
      for (const [key, raw] of searchParams.entries()) {
        if (isOwnedKey(key, namespace)) continue;
        merged.set(key, raw);
      }
      for (const [key, raw] of owned.entries()) {
        merged.set(key, raw);
      }
      // `replace: true` keeps history compact — back button still
      // moves between meaningful page boundaries, not chip-by-chip.
      setSearchParams(merged, { replace: true });
      writeTimerRef.current = null;
    }, URL_WRITE_DEBOUNCE_MS);
    return () => {
      if (writeTimerRef.current) {
        clearTimeout(writeTimerRef.current);
        writeTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, setSearchParams, namespace]);

  // setChips: filter mutation. Drops cursor per the cursor-lifecycle
  // invariant.
  const setChips = useCallback((nextChips) => {
    setState((s) => ({
      ...s,
      chips: typeof nextChips === 'function' ? nextChips(s.chips) : nextChips,
      cursor: null,
    }));
  }, []);

  // setSort: explicit sort change. Drops cursor; no debounce nuance —
  // the standard debounce above still applies. Per spec, this is an
  // explicit user action, but the debounce timer is short enough that
  // the "no debounce" requirement is effectively met.
  const setSort = useCallback((nextSort) => {
    setState((s) => ({
      ...s,
      sort: VALID_SORTS.has(nextSort) ? nextSort : 'lastChangedAt:desc',
      cursor: null,
    }));
  }, []);

  // setCursor: "Load more" handler. The cursor is the one returned by
  // the previous fetch's `nextCursor`.
  const setCursor = useCallback((nextCursor) => {
    setState((s) => ({ ...s, cursor: nextCursor || null }));
  }, []);

  // setView: saved-view click. Per the cursor-lifecycle invariant,
  // dropping into a view clears the cursor.
  const setView = useCallback((nextView) => {
    setState((s) => ({ ...s, view: nextView || null, cursor: null }));
  }, []);

  // clearAll: reset to baseline (scope chip only, default sort, no cursor).
  const clearAll = useCallback(() => {
    setState(() => ({
      chips: chipsFromSearchParams(new URLSearchParams()),
      sort: 'lastChangedAt:desc',
      cursor: null,
      view: null,
    }));
  }, []);

  return useMemo(() => ({
    chips: state.chips,
    sort: state.sort,
    cursor: state.cursor,
    view: state.view,
    setChips,
    setSort,
    setCursor,
    setView,
    clearAll,
  }), [state, setChips, setSort, setCursor, setView, clearAll]);
}
